import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpResponsesHandlersFactory } from '../handlers/http-responses-handler';
import { BaseDataService } from './base.data.service';
import { IWrapUpCallReason } from '../interfaces/wrapup-call-reason.interface';
import { IOrganisationPhoneNumber } from '../interfaces/telephony/organisation-phone-number';
import {
  ICurrentTelephonyUser,
  ITelephonyUser,
} from '../interfaces/telephony/telephony-user.interface';
import { ICallStateData } from '../interfaces/telephony/call-state.data';
import { ILogCallDTO } from '../interfaces/telephony/log-call.dto.interface';
import { BehaviorSubject } from 'rxjs';
import localforage from 'localforage';
import { ConfirmationService } from 'primeng/api';
import { CallEventSubscription } from '../interfaces/telephony/call-event-subscription';

export const LOCAL_STORAGE_CALL_STATE = 'call_state';
export const LOCAL_STORAGE_RECENT_CALLS = 'recent_calls';

@Injectable({ providedIn: 'root' })
export class TelephonyService extends BaseDataService {
  private url = '/api/telephony';

  public static EventState_Direction_Incoming = 'inbound';
  public static EventState_Direction_Outgoing = 'outbound';
  public static EventState_Ringing = 'ringing';
  public static EventState_Calling = 'calling';
  public static EventState_InCall = 'connected';
  public static EventState_EndCall = 'hangup';
  public static EventState_OnHold = 'hold';

  public CurrentCallState$: BehaviorSubject<ICallStateData> = new BehaviorSubject<ICallStateData>(
    null,
  );

  public RecentCalls$: BehaviorSubject<ICallStateData[]> = new BehaviorSubject<ICallStateData[]>(
    [],
  );

  public AnonCallLoggable$ = new BehaviorSubject<boolean>(false);

  public IdentifiedCallLoggable$ = new BehaviorSubject<boolean>(false);

  constructor(
    http: HttpClient,
    handlersFactory: HttpResponsesHandlersFactory,
    private confirmationService: ConfirmationService,
  ) {
    super(http, handlersFactory);
    void this.initializeCallState();
  }

  private async initializeCallState() {
    const savedCallState = await this.GetCurrentCallState();
    this.SetCurrentCallState(savedCallState);
    this.updateCallLoggable(savedCallState);
    const savedRecentCalls = await this.GetRecentCalls();
    this.RecentCalls$.next(savedRecentCalls || []);
  }

  private updateCallLoggable(callState: ICallStateData) {
    this.AnonCallLoggable$.next(!!callState);
    this.IdentifiedCallLoggable$.next(!!callState && !!callState.callerInfo?.organisationId);
  }

  public async GetCurrentCallState(): Promise<ICallStateData> {
    return localforage.getItem<ICallStateData>(LOCAL_STORAGE_CALL_STATE);
  }

  public SetCurrentCallState(value: ICallStateData) {
    this.CurrentCallState$.next(value);
    if (value) {
      void localforage.setItem<ICallStateData>(LOCAL_STORAGE_CALL_STATE, value);
      void this.addOrUpdateRecentCall(value);
    } else {
      void localforage.removeItem(LOCAL_STORAGE_CALL_STATE);
    }
    this.updateCallLoggable(value);
  }

  public async GetRecentCalls(): Promise<ICallStateData[]> {
    return localforage
      .getItem<ICallStateData[]>(LOCAL_STORAGE_RECENT_CALLS)
      .then((calls) => calls || []);
  }

  public async SaveRecentCalls(calls: ICallStateData[]) {
    this.RecentCalls$.next(calls);
    await localforage.setItem<ICallStateData[]>(LOCAL_STORAGE_RECENT_CALLS, calls);
  }

  private async addOrUpdateRecentCall(call: ICallStateData) {
    if (!call || !call.latestEvent || !call.latestEvent.call_id) {
      return;
    }

    const recentCalls = await this.GetRecentCalls();
    const existingIndex = recentCalls.findIndex(
      (c) => c.latestEvent?.call_id === call.latestEvent.call_id,
    );

    if (existingIndex > -1) {
      const existingCall = recentCalls[existingIndex];
      recentCalls[existingIndex] = {
        ...existingCall,
        ...call,
        latestEvent: {
          ...existingCall.latestEvent,
          ...call.latestEvent,
        },
        callerInfo: call.callerInfo || existingCall.callerInfo,
      };
    } else {
      recentCalls.unshift(call);
    }

    const getCallTime = (c: ICallStateData) => {
      const dateStr = c.latestEvent?.date_ended || c.latestEvent?.date_started;
      return dateStr ? new Date(dateStr).getTime() : 0;
    };

    // Sort by end date descending (falling back to start date if not ended yet)
    recentCalls.sort((a, b) => getCallTime(b) - getCallTime(a));

    // Filter out calls that are older than 30 minutes
    const now = new Date().getTime();
    const thirtyMinutesMs = 30 * 60 * 1000;

    let filteredCalls = recentCalls.filter((c) => {
      const callTime = getCallTime(c);
      if (callTime === 0) {
        return true; // Keep calls without timestamps in the list just in case
      }
      return now - callTime <= thirtyMinutesMs;
    });

    // Ensure we do not clear the entire list if there was at least one call originally
    if (filteredCalls.length === 0 && recentCalls.length > 0) {
      filteredCalls.push(recentCalls[0]);
    } else if (
      recentCalls.length > 0 &&
      !filteredCalls.some((c) => c.latestEvent?.call_id === recentCalls[0].latestEvent?.call_id)
    ) {
      filteredCalls.unshift(recentCalls[0]);
    }

    await this.SaveRecentCalls(filteredCalls);
  }

  public getCurrentTelephonyUser(): Promise<ICurrentTelephonyUser> {
    return this.http.get<ICurrentTelephonyUser>(`${this.url}/current-user`).toPromise();
  }

  public getTelephonyUsers(): Promise<ITelephonyUser[]> {
    return this.http.get<ITelephonyUser[]>(`${this.url}/getTelephonyUsers`).toPromise();
  }

  public updateTelephonyUser(userDTO: ITelephonyUser): Promise<any> {
    return this.http.put(`${this.url}/user`, userDTO).toPromise();
  }

  public createTelephonyStat(stat) {
    return this.handleRequest(
      this.http.post(`${this.url}/createStat`, stat),
      this.handlersFactory.getDefault(),
    );
  }

  public delistNumber(number: string) {
    return this.handleRequest(
      this.http.post(`${this.url}/delistNumber/${number}`, {}),
      this.handlersFactory.getDefault(),
    );
  }

  public getDelistedNumbers(): Promise<IOrganisationPhoneNumber[]> {
    return this.http.get<IOrganisationPhoneNumber[]>(`${this.url}/delistedNumbers`).toPromise();
  }

  public logCall(logDTO: ILogCallDTO): Promise<any> {
    return this.handleRequest(
      this.http.post(`${this.url}/log-call`, logDTO),
      this.handlersFactory.getDefault(),
    );
  }

  public setCallStatCreated() {
    const currentCallState = this.CurrentCallState$.value;
    const newState: ICallStateData = {
      ...currentCallState,
      latestEvent: {
        ...currentCallState.latestEvent,
        call_stat_created: true,
      },
    };
    this.SetCurrentCallState(newState);
  }

  public setOrganisationPhoneNumber(dto: IOrganisationPhoneNumber) {
    return this.http.post(`${this.url}/setOrganisationPhoneNumber`, dto).toPromise();
  }

  public updateOrganisationPhoneNumber(id: number, phoneNumber: string) {
    return this.handleRequest(
      this.http.post(`${this.url}/updateOrganisationPhoneNumber`, {
        Id: id,
        PhoneNumber: phoneNumber,
      }),
      this.handlersFactory.getDefault(),
    );
  }

  public deleteTelephoneNumber(orgId: number, id: number) {
    return this.handleRequest(
      this.http.delete(`${this.url}/${orgId}/organisationPhoneNumber/${id}`),
      this.handlersFactory.getDefault(),
    );
  }

  public getPhoneNumberData(phoneNumber: string): Promise<IOrganisationPhoneNumber[]> {
    return this.http
      .get<IOrganisationPhoneNumber[]>(
        `${this.url}/get-number-data?number=${encodeURIComponent(phoneNumber)}`,
      )
      .toPromise();
  }

  public getWarnings(): Promise<any> {
    return this.http.get<any>(`${this.url}/get-warnings`).toPromise();
  }

  public getCallEventSubscriptions(): Promise<CallEventSubscription[]> {
    return this.http
      .get<CallEventSubscription[]>(`${this.url}/call-event-subscriptions`)
      .toPromise();
  }

  public setupCallEventSubscriptions(): Promise<any> {
    return this.http.post<any>(`${this.url}/call-event-subscriptions/setup`, {}).toPromise();
  }

  async checkExistingNumberAssignment(
    phoneNumber: string,
    currentId: number | null = null,
    orgId: number,
  ): Promise<boolean> {
    const existing = await this.getPhoneNumberData(phoneNumber);

    // filter out the current item if an id is provided
    let conflictingAssignments = existing.filter((x) => currentId === null || x.id !== currentId);

    // filter out items with same orgid
    conflictingAssignments = conflictingAssignments.filter((x) => x.organisationId != orgId);

    if (conflictingAssignments.length === 0) {
      return true;
    }

    const msg = conflictingAssignments.map((x) => {
      return (
        x.organisationName +
        ' ' +
        (!x.organisationActive ? '(Inactive)' : '') +
        (x.userName ? ', ' + x.userName : '') +
        (x.userName && !x.userActive ? ' (Inactive)' : '') + // Only show (Inactive) if userName exists
        '\n'
      );
    });

    return new Promise<boolean>((resolve) => {
      this.confirmationService.confirm({
        header: 'Overwrite number assignment',
        icon: 'pi pi-exclamation-triangle',
        message: `The number '${phoneNumber}' is already assigned to ${msg}\n are you sure you want to reassign it?`,
        acceptLabel: 'Reassign',
        accept: () => resolve(true),
        reject: () => resolve(false),
      });
    });
  }

  syncTelephonyUsers(): Promise<any> {
    return this.http.post<any>(`${this.url}/sync-telephony-users`, {}).toPromise();
  }
}
